import {renderDocxHtml} from './renderDocxHtml';
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Box, Paper } from '@mui/material';
import fileService from '../services/fileService';
import api from '../utils/api';
import { FileItem } from '../types/files';
import PreviewCanvas from '../components/preview/PreviewCanvas';
import PreviewSkeleton from '../components/preview/PreviewSkeleton';
import PreviewEmptyState from '../components/preview/PreviewEmptyState';
import TextPreviewPanel from '../components/preview/TextPreviewPanel';
import {
  getPreviewMimeType,
  getPreviewCategory,
  extensionOf,
  needsTokenizedPreviewUrl,
  isTransparentImageMime,
  getFriendlyFileType,
  type PreviewCategory,
} from '../utils/previewUtils';
import { isSpreadsheetFile, openFileInGrid } from '../utils/gridIntegration';
import TableChartIcon from '@mui/icons-material/TableChart';

export interface PreviewEngineProps {
  file: Pick<FileItem, 'id' | 'name' | 'size'> & {
    mimetype?: string;
    mimeType?: string;
    metadata?: { fileFormat?: string };
  };
  versionId?: string;
  imageZoom?: number;
  imageRotation?: number;
  pdfScale?: number;
  compact?: boolean;
  textFontSize?: number;
  textWordWrap?: boolean;
  /** Build preview URL for shared links (bypasses fileService token flow) */
  sharePreviewUrlBuilder?: (
    fileId: string,
    format?: string
  ) => string | Promise<string>;
  /** Share-page download (uses public share API instead of authenticated fileService) */
  onShareDownload?: () => void | Promise<void>;
  onLoadingChange?: (loading: boolean) => void;
  onCategoryChange?: (category: PreviewCategory) => void;
  onMediaUrlChange?: (url: string | null) => void;
  onTextCopy?: () => void;
}

export interface PreviewEngineHandle {
  retry: () => void;
  getMediaUrl: () => string | null;
  getTextContent: () => string;
}

export const PreviewEngine = forwardRef<PreviewEngineHandle, PreviewEngineProps>(
  (
    {
      file,
      versionId,
      imageZoom = 1,
      imageRotation = 0,
      pdfScale = 1,
      compact = false,
      textFontSize = 14,
      textWordWrap = true,
      sharePreviewUrlBuilder,
      onShareDownload,
      onLoadingChange,
      onCategoryChange,
      onMediaUrlChange,
    },
    ref
  ) => {
    const [fetchKey, setFetchKey] = useState(0);
    const mimeType = useMemo(() => getPreviewMimeType(file), [file]);
    const ext = extensionOf(file.name);
    const category = useMemo(() => getPreviewCategory(mimeType, ext), [mimeType, ext]);

    const [mediaUrl, setMediaUrl] = useState<string | null>(null);
    const [docxHtml, setDocxHtml] = useState<string | null>(null);
    const [urlLoading, setUrlLoading] = useState(false);
    const [textContent, setTextContent] = useState('');
    const [textTruncated, setTextTruncated] = useState(false);
    const [textLoading, setTextLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [iframeLoaded, setIframeLoaded] = useState(false);

    const isDocx = category === 'docx';
    const isTextLike = category === 'text';
    const isOffice = category === 'office';
    const needsTokenizedUrl = needsTokenizedPreviewUrl(mimeType);

    const loading = textLoading || urlLoading || (Boolean(mediaUrl) && mimeType.startsWith('image/') && !imageLoaded) || ((isDocx || mimeType === 'application/pdf') && !iframeLoaded && !error);

    useEffect(() => {
      onCategoryChange?.(category);
    }, [category, onCategoryChange]);

    useEffect(() => {
      onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);

    useEffect(() => {
      onMediaUrlChange?.(mediaUrl);
    }, [mediaUrl, onMediaUrlChange]);

    const buildUrl = useCallback(
      async (format?: string, optimize = true) => {
        if (sharePreviewUrlBuilder) {
          return sharePreviewUrlBuilder(file.id, format);
        }
        return fileService.buildPreviewUrl(file.id, format,
          {mode:'preview', optimize: format === 'html' ? false : optimize}, {withAccessToken:true,versionId});
      },
      [file.id, sharePreviewUrlBuilder, versionId]
    );

    useEffect(() => {
      let cancelled = false;
      setError(null);
      setMediaUrl(null);
      setDocxHtml(null);
      setTextContent('');
      setTextTruncated(false);
      setImageLoaded(false);
      setIframeLoaded(false);

      if (!mimeType || isOffice) {
        return undefined;
      }

      if (isDocx) {
        const controller=new AbortController();
        setUrlLoading(true);
        (async()=>{
          try{
            if(file.size>20*1024*1024)throw new Error('Word preview exceeds 20 MB. Download the original document.');
            const url=await buildUrl(undefined,false);
            const response=await fetch(url,{signal:controller.signal,redirect:'error'});
            if(!response.ok)throw new Error(`Preview request failed (${response.status})`);
            const data=await response.arrayBuffer();
            if(cancelled)return;
            const html=await renderDocxHtml(data);
            if(!cancelled)setDocxHtml(html);
          }catch(error){if(!cancelled)setError(error instanceof Error?error.message:'Failed to render Word document');}
          finally{if(!cancelled)setUrlLoading(false);}
        })();
        return()=>{cancelled=true;controller.abort();};
      }

      if (isTextLike) {
        setTextLoading(true);
        (async () => {
          try {
            if (sharePreviewUrlBuilder) {
              const url = await buildUrl();
              const res = await fetch(url);
              const text = await res.text();
              if (!cancelled) {
                setTextContent(text);
                setTextTruncated(false);
              }
            } else {
              const params = new URLSearchParams({ format: 'json' });
              if (versionId) params.append('versionId', versionId);
              const { data } = await api.get(`/files/${file.id}/preview?${params.toString()}`);
              if (cancelled) return;
              if (data?.preview?.content != null) {
                setTextContent(data.preview.content);
                setTextTruncated(Boolean(data.preview.truncated));
              } else {
                setError('Invalid text preview response');
              }
            }
          } catch {
            if (!cancelled) setError('Failed to load text preview');
          } finally {
            if (!cancelled) setTextLoading(false);
          }
        })();
        return () => {
          cancelled = true;
        };
      }

      if (!needsTokenizedUrl) {
        return undefined;
      }

      setUrlLoading(true);
      (async () => {
        try {
          const url = await buildUrl();
          if (!cancelled) setMediaUrl(url);
        } catch {
          if (!cancelled) setError('Failed to build preview URL');
        } finally {
          if (!cancelled) setUrlLoading(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [
      file.id,
      mimeType,
      isOffice,
      isDocx,
      isTextLike,
      needsTokenizedUrl,
      versionId,
      fetchKey,
      buildUrl,
      sharePreviewUrlBuilder,
    ]);

    const retry = useCallback(() => {
      setFetchKey((k) => k + 1);
    }, []);

    useImperativeHandle(ref, () => ({
      retry,
      getMediaUrl: () => mediaUrl,
      getTextContent: () => textContent,
    }));

    const handleDownload = useCallback(async () => {
      if (onShareDownload) {
        await onShareDownload();
        return;
      }
      if (sharePreviewUrlBuilder) {
        try {
          const rawUrl = await sharePreviewUrlBuilder(file.id);
          const u = new URL(String(rawUrl), window.location.origin);
          u.searchParams.delete('inline');
          const response = await fetch(u.toString());
          if (!response.ok) throw new Error('Download failed');
          const blob = await response.blob();
          const objectUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.setAttribute('download', file.name);
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(objectUrl);
          document.body.removeChild(link);
        } catch {
          // share download failed
        }
        return;
      }
      if (versionId) await fileService.downloadFileVersion(file.id,versionId);
      else await fileService.downloadFile(file.id);
    }, [file.id, file.name, versionId, onShareDownload, sharePreviewUrlBuilder]);

    const mediaMaxHeight = compact ? 280 : '80vh';
    const canvasHeight = compact ? 360 : '80vh';

    if (!mimeType) {
      return (
        <PreviewCanvas compact={compact} loading={false}>
          <PreviewEmptyState
            fileName={file.name}
            title="File type not available"
            subtitle="Could not determine the file type for preview."
            onDownload={handleDownload}
          />
        </PreviewCanvas>
      );
    }

    if (isOffice) {
      const friendlyType = getFriendlyFileType(file.name, mimeType);
      const opensInGrid = isSpreadsheetFile(file.name, mimeType);
      const isSpreadsheetLike =
        opensInGrid ||
        extensionOf(file.name) === 'xls' ||
        friendlyType === 'Excel spreadsheet' ||
        friendlyType === 'CSV spreadsheet';

      return (
        <PreviewCanvas compact={compact} aria-label={`Preview of ${file.name}`}>
          <PreviewEmptyState
            fileName={file.name}
            mimeType={mimeType}
            size={file.size}
            variant="office"
            title={file.name}
            typeLabel={friendlyType}
            subtitle={
              opensInGrid
                ? 'This spreadsheet opens in CYNAYD Grid.'
                : isSpreadsheetLike
                  ? 'Preview isn’t available in Drive. Download the file to open it.'
                  : 'Preview isn’t available in Drive.'
            }
            primaryAction={
              opensInGrid && file.id
                ? {
                    label: 'Open in Grid',
                    onClick: () => openFileInGrid(file.id!),
                    startIcon: <TableChartIcon />,
                  }
                : undefined
            }
            onDownload={handleDownload}
          />
        </PreviewCanvas>
      );
    }

    if (error && !textLoading && !urlLoading) {
      return (
        <PreviewCanvas compact={compact}>
          <PreviewEmptyState
            fileName={file.name}
            mimeType={mimeType}
            size={file.size}
            variant="error"
            title="Couldn't load preview"
            subtitle={error}
            onRetry={retry}
            onDownload={handleDownload}
            onOpenNewTab={mediaUrl ? () => window.open(mediaUrl, '_blank') : undefined}
          />
        </PreviewCanvas>
      );
    }

    const showSkeleton = textLoading || urlLoading;

    if (isTextLike) {
      return (
        <PreviewCanvas compact={compact} loading={textLoading} aria-label={`Text preview of ${file.name}`}>
          {textLoading ? (
            <PreviewSkeleton category="text" compact={compact} />
          ) : (
            <TextPreviewPanel
              content={textContent}
              fileName={file.name}
              truncated={textTruncated}
              compact={compact}
              fontSize={textFontSize}
              wordWrap={textWordWrap}
            />
          )}
        </PreviewCanvas>
      );
    }

    return (
      <PreviewCanvas
        compact={compact}
        loading={showSkeleton}
        checkerboard={category === 'image' && isTransparentImageMime(mimeType)}
        aria-label={`Preview of ${file.name}`}
      >
        {showSkeleton ? (
          <PreviewSkeleton category={category} compact={compact} />
        ) : null}

        {!showSkeleton && isDocx && docxHtml && (
          <Box sx={{ width: '100%', height: canvasHeight, opacity: iframeLoaded ? 1 : 0, transition: 'opacity 0.2s' }}>
            <iframe
              title={file.name}
              srcDoc={docxHtml}
              sandbox=""
              style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
              onLoad={() => setIframeLoaded(true)}
            />
          </Box>
        )}

        {!showSkeleton && mimeType.startsWith('image/') && mediaUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: compact ? 1 : 2, width: '100%' }}>
            <Box
              component="img"
              src={mediaUrl}
              alt={file.name}
              onLoad={() => setImageLoaded(true)}
              onError={() => {
                setImageLoaded(false);
                setError('Failed to load preview image');
              }}
              sx={{
                maxWidth: '100%',
                maxHeight: mediaMaxHeight,
                objectFit: 'contain',
                transform: `scale(${imageZoom}) rotate(${imageRotation}deg)`,
                transition: 'transform 0.2s, opacity 0.25s',
                opacity: imageLoaded ? 1 : 0,
              }}
            />
          </Box>
        )}

        {!showSkeleton && mimeType === 'application/pdf' && mediaUrl && (
          <Box
            sx={{
              width: '100%',
              height: canvasHeight,
              overflow: 'auto',
              display: 'flex',
              justifyContent: 'center',
              transform: `scale(${pdfScale})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s',
            }}
          >
            <Box sx={{ width: '100%', height: '100%', minHeight: canvasHeight, opacity: iframeLoaded ? 1 : 0 }}>
              <iframe
                src={`${mediaUrl}#toolbar=0&view=FitH`}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title={file.name}
                onLoad={() => setIframeLoaded(true)}
              />
            </Box>
          </Box>
        )}

        {!showSkeleton && mimeType.startsWith('video/') && mediaUrl && (
          <Paper elevation={2} sx={{ maxWidth: '100%', width: compact ? '100%' : 'min(900px, 95%)', p: 1 }}>
            <Box
              component="video"
              controls
              sx={{ width: '100%', maxHeight: mediaMaxHeight, display: 'block' }}
            >
              <source src={mediaUrl} type={mimeType} />
              Your browser does not support the video tag.
            </Box>
          </Paper>
        )}

        {!showSkeleton && mimeType.startsWith('audio/') && mediaUrl && (
          <Paper elevation={2} sx={{ width: compact ? '100%' : 'min(480px, 90%)', p: 3 }}>
            <Box component="audio" controls sx={{ width: '100%' }}>
              <source src={mediaUrl} type={mimeType} />
              Your browser does not support the audio tag.
            </Box>
          </Paper>
        )}

        {!showSkeleton &&
          !isDocx &&
          !mediaUrl &&
          !error &&
          category === 'unsupported' && (
            <PreviewEmptyState
              fileName={file.name}
              mimeType={mimeType}
              size={file.size}
              title="Preview not available"
              subtitle="This file type cannot be previewed in the browser."
              onDownload={handleDownload}
            />
          )}
      </PreviewCanvas>
    );
  }
);

PreviewEngine.displayName = 'PreviewEngine';

export default PreviewEngine;
